import React from 'react';
import { Signal } from '@demind-inc/core';
import { UnifiedChatRichContextEntry } from '../../../data-access/types';
import ContentCard from '../RichContext/ContentCard';
import LocationRow from '../RichContext/LocationRow';
import {
  getMealDetailsFromRichContextSignals,
  getMealSummaryPayloadFromSignals,
} from '../RichContext/mealSignalUtils';
import SignalPill from '../RichContext/SignalPill';
import WeatherSignalCard from '../RichContext/WeatherSignalCard';
import { isSleepSummaryLike, normalizeRichContentType } from '../RichContext/contentCardUtils';
import '../RichContext/richContext.scss';

interface AssistantChatRichContextCardProps {
  entries: UnifiedChatRichContextEntry[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const hasMeaningfulValue = (value: unknown): boolean => {
  if (value == null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value === 'boolean') return true;
  if (Array.isArray(value)) return value.some(hasMeaningfulValue);
  if (typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).some(hasMeaningfulValue);
  }
  return false;
};

const hasMeaningfulPayload = (payload: unknown): boolean => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return false;
  return hasMeaningfulValue(payload);
};

const hasRenderableContentItem = (content: unknown): boolean => {
  if (!content || typeof content !== 'object') return false;
  const raw = content as Record<string, unknown>;
  const type = typeof raw.type === 'string' ? raw.type.trim() : '';
  if (!type) return false;
  return hasMeaningfulPayload(raw.payload);
};

const isSleepSummaryContentItem = (content: unknown): boolean => {
  if (!content || typeof content !== 'object') return false;
  const raw = content as Record<string, unknown>;
  const type = normalizeRichContentType(typeof raw.type === 'string' ? raw.type : '');
  return type === 'sleep_summary';
};

const isEnergySummaryContentItem = (content: unknown): boolean => {
  if (!content || typeof content !== 'object') return false;
  const raw = content as Record<string, unknown>;
  const type = typeof raw.type === 'string' ? raw.type.trim().toLowerCase() : '';
  return type === 'energy' || type === 'energy_summary';
};

const isMealSuggestionContentItem = (content: unknown): boolean => {
  if (!content || typeof content !== 'object') return false;
  const raw = content as Record<string, unknown>;
  const type = typeof raw.type === 'string' ? raw.type.trim().toLowerCase() : '';
  return type === 'meal_suggestion' || type === 'meal_content';
};

const splitMealContentItem = (
  content: unknown
): Array<{ type: string; payload: Record<string, unknown> }> => {
  if (!content || typeof content !== 'object') return [];
  const raw = content as Record<string, unknown>;
  const type = typeof raw.type === 'string' ? raw.type.trim().toLowerCase() : '';
  const payload =
    raw.payload && typeof raw.payload === 'object' && !Array.isArray(raw.payload)
      ? (raw.payload as Record<string, unknown>)
      : null;

  const effectiveType =
    type ||
    (payload && typeof payload.meal_content === 'object' && payload.meal_content !== null
      ? 'meal_suggestion'
      : '');

  if (effectiveType !== 'meal_suggestion' || !payload)
    return [raw as { type: string; payload: Record<string, unknown> }];

  const mealContent =
    payload.meal_content &&
    typeof payload.meal_content === 'object' &&
    !Array.isArray(payload.meal_content)
      ? (payload.meal_content as Record<string, unknown>)
      : null;

  if (mealContent) {
    return [{ type: 'meal_suggestion', payload: { meal_content: mealContent } }];
  }
  return [{ type: 'meal_suggestion', payload: { ...payload } }];
};

const getSleepSummaryFromSignals = (
  richContext: UnifiedChatRichContextEntry['richContext'] | null | undefined
): Record<string, unknown> | null => {
  if (!richContext || !Array.isArray(richContext.signals)) return null;
  for (const signal of richContext.signals) {
    const rawSource = signal?.signal_source;
    const source = typeof rawSource === 'string' ? rawSource.trim().toLowerCase() : '';
    if (source !== 'sleep' && source !== 'sleepsummary') continue;
    const data = signal?.data;
    if (!data || typeof data !== 'object' || Array.isArray(data)) continue;
    const rawData = data as Record<string, unknown>;
    const nestedSleepSummary =
      rawData.sleep_summary &&
      typeof rawData.sleep_summary === 'object' &&
      !Array.isArray(rawData.sleep_summary)
        ? (rawData.sleep_summary as Record<string, unknown>)
        : rawData.sleepsummary &&
            typeof rawData.sleepsummary === 'object' &&
            !Array.isArray(rawData.sleepsummary)
          ? (rawData.sleepsummary as Record<string, unknown>)
          : null;
    if (nestedSleepSummary && isSleepSummaryLike(nestedSleepSummary)) return nestedSleepSummary;
    if (isSleepSummaryLike(rawData)) return rawData;
  }
  return null;
};

const getRenderableContentList = (
  richContext: UnifiedChatRichContextEntry['richContext'] | null | undefined
): Array<{ type: string; payload: Record<string, unknown> }> => {
  if (!richContext || typeof richContext !== 'object') return [];

  const rawContent = richContext.content;
  const contentList: unknown[] = rawContent
    ? Array.isArray(rawContent)
      ? [...rawContent]
      : [rawContent]
    : [];

  const mealPayload = getMealSummaryPayloadFromSignals(richContext);
  const signalMealDetails = getMealDetailsFromRichContextSignals(richContext);
  if (mealPayload) {
    contentList.push({ type: 'meal_content', payload: mealPayload });
  } else if (signalMealDetails.length > 0) {
    contentList.push({ type: 'meal_content', payload: { meal_details: signalMealDetails } });
  }

  const hasSleepSummaryContent = contentList.some(isSleepSummaryContentItem);
  const sleepSummarySignalPayload = hasSleepSummaryContent
    ? null
    : getSleepSummaryFromSignals(richContext);
  if (sleepSummarySignalPayload) {
    contentList.push({ type: 'sleep_summary', payload: sleepSummarySignalPayload });
  }

  return contentList.flatMap(splitMealContentItem).filter(hasRenderableContentItem) as Array<{
    type: string;
    payload: Record<string, unknown>;
  }>;
};

const hasRichContent = (entry: UnifiedChatRichContextEntry): boolean => {
  const { richContext } = entry;
  if (!richContext) return false;
  const hasSignals = Array.isArray(richContext.signals) && richContext.signals.length > 0;
  const contentList = getRenderableContentList(richContext);
  const hasContent = contentList.length > 0;
  const hasLocation = entry.source !== 'adjustment' && !!richContext.location;
  return hasSignals || hasContent || hasLocation;
};

// ─── Main card ────────────────────────────────────────────────────────────────

const AssistantChatRichContextCard: React.FC<AssistantChatRichContextCardProps> = ({ entries }) => {
  const visibleEntries = entries?.filter(hasRichContent) ?? [];
  if (!visibleEntries.length) return null;

  return (
    <div className="rc-container">
      {visibleEntries.map((entry) => {
        const { richContext, suggestionId } = entry;
        const signals: Signal[] = Array.isArray(richContext?.signals) ? richContext!.signals : [];
        const contentList = getRenderableContentList(richContext);
        const location = richContext?.location;
        const locationLabel = (location?.name as string | undefined) ?? location?.name ?? null;

        const isEnrichmentReadyStatus =
          typeof richContext?.status_text === 'string' &&
          /(ready|completed?|done)/i.test(richContext.status_text);
        const rawEnrichmentPending =
          Boolean(richContext?.enrichment_pending) && !isEnrichmentReadyStatus;
        const enrichmentStatusText = richContext?.status_text;

        const weatherSignals = signals.filter((s) => s.signal_source === 'weather');
        const hasSleepSummaryContent = contentList.some(isSleepSummaryContentItem);
        const hasEnergySummaryContent = contentList.some(isEnergySummaryContentItem);
        const hasMealSuggestionContent = contentList.some(isMealSuggestionContentItem);
        const lastContent = contentList.length > 0 ? contentList[contentList.length - 1] : null;
        const enrichmentPending =
          rawEnrichmentPending && !(lastContent && hasMeaningfulPayload(lastContent.payload));

        const otherSignals = signals.filter((s) => {
          const source =
            typeof s.signal_source === 'string' ? s.signal_source.trim().toLowerCase() : '';
          if (s.signal_source === 'weather') return false;
          if (hasSleepSummaryContent && (source === 'sleep' || source === 'sleepsummary'))
            return false;
          if (hasEnergySummaryContent && s.signal_source === 'energy') return false;
          if (hasMealSuggestionContent && s.signal_source === 'meal') return false;
          return true;
        });

        const hasVisibleSections =
          weatherSignals.length > 0 ||
          otherSignals.length > 0 ||
          (!!locationLabel && entry.source !== 'adjustment') ||
          contentList.length > 0;

        if (!hasVisibleSections) return null;

        return (
          <div key={suggestionId} className="rc-card">
            {weatherSignals.map((signal, i) => (
              <WeatherSignalCard key={`${suggestionId}_weather_${i}`} signal={signal} />
            ))}

            {otherSignals.length > 0 && (
              <div className="rc-signals-row">
                {otherSignals.slice(0, 5).map((signal, i) => (
                  <SignalPill key={`${suggestionId}_sig_${i}`} signal={signal} />
                ))}
              </div>
            )}

            {!!locationLabel && entry.source !== 'adjustment' && (
              <LocationRow locationLabel={locationLabel} />
            )}

            {contentList.length > 0 && (
              <div className="rc-content-list">
                {contentList.map((c, i) => (
                  <ContentCard
                    key={`${suggestionId}_content_${i}`}
                    type={c.type}
                    payload={c.payload}
                    enrichmentPending={enrichmentPending && i === contentList.length - 1}
                    enrichmentStatusText={enrichmentPending ? enrichmentStatusText : undefined}
                    fromAssistantRichContextCard
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AssistantChatRichContextCard;
